// src/components/ProductModal.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Product, Customer, displayPrice, useHandleAddToCart, Modifier, formatProductNameForUrl } from '../utils/types'; // Ensure Modifier is imported
import { toast } from 'react-toastify';
import CustomerProfileModal from './CustomerProfileModal';
import { FiX } from 'react-icons/fi'; // Import close icon
import ModifierModal from './ModifierModal'; // Import ModifierModal if used within ProductModal
import ProductDetails from './ProductDetails'; // Import the new component
import { IconContext } from "react-icons";
import { FaShieldHeart, FaLink, FaFacebook, FaTelegram, FaLine, FaTwitter, FaWhatsapp } from "react-icons/fa6";
import { IoLogoWechat } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useTranslation } from 'react-i18next';

// Define a type for the price data expected from the parent
interface MTPriceData {
    price: number;
    role: string; // The role for which this price applies
}

// Define props for ProductModal, including the new ones
interface ProductModalProps {
    product: Product;
    onClose: () => void;
    // Add props passed down from ProductCard or parent
    customerForAction: Customer | null;
    isLoadingCustomerForAction: boolean;
    mtPriceData: MTPriceData | null | undefined;
    effectiveUserId: number | undefined;
    effectiveUserRole: string | undefined;
    effectiveUserIsAuthenticated: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
    product,
    onClose,
    // Destructure new props
    customerForAction,
    isLoadingCustomerForAction,
    mtPriceData,
    effectiveUserId,
    effectiveUserRole,
    effectiveUserIsAuthenticated
}) => {
    const settings = JSON.parse(localStorage.getItem('settings') || '{}'); // Get settings from local storage

    const { t } = useTranslation(); // For translations 

    // Use the hook for cart actions AND modifier handling if needed within this modal
    const {
        handleAddToCart,
        showModifierModal,
        handleCloseModifierModal,
        modifiers,
        selectedProduct,
        handleModifierSelection,
        handleConfirmModifiers,
        selectedModifiers
    } = useHandleAddToCart();

    const imageRef = useRef<HTMLImageElement>(null); // Ref for potential animation
    const modalContentRef = useRef<HTMLDivElement>(null); // Ref for the modal content itself

    const [quantity, setQuantity] = useState(1); // State for quantity


    // --- State for Customer Profile Modal ---
    const [showCustomerProfileModal, setShowCustomerProfileModal] = useState(false);

    // --- Add to Cart Handler specific to this modal ---
    const handleModalAddToCartClick = () => {
        // 1. Check Authentication (using passed prop)
        if (!effectiveUserIsAuthenticated) {
            toast.error("Please log in to add items to CART.");
            return;
        }

        // 2. Check loading state (using passed prop)
        if (isLoadingCustomerForAction) {
            toast.info("Checking customer details...");
            return;
        }

        // 3. Check customer data and profile completeness (using passed prop)
        if (!customerForAction) {
            toast.error("Could not verify customer details. Please try again or contact support.");
            console.error("Modal Add to cart check failed: customerForAction is null");
            return;
        }
        const hasPhone = customerForAction.phone && String(customerForAction.phone).trim() !== '';
        const hasEmail = customerForAction.email && String(customerForAction.email).trim() !== '';

        if (!hasPhone || !hasEmail) {
            // 4. Show profile modal
            toast.warn("Please complete your profile (Phone and Email) before adding items.");
            setShowCustomerProfileModal(true);
        } else {
            // 5. Proceed with adding to cart (using the handleAddToCart from the hook)
            // Use the price/user details passed down
            const priceToAdd = mtPriceData?.price ?? product.product_value;
            // Pass the imageRef if you intend to use it for animation from the modal
            // handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole);

            handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole, quantity);

            // Optionally close the ProductModal after adding
            // onClose();
        }
    };

    // --- Handler to close the Customer Profile Modal ---
    const handleCustomerProfileModalClose = () => {
        setShowCustomerProfileModal(false);
    };

    // --- Quantity Change Handler ---
    const handleQuantityChange = (newQuantity: number) => {
        setQuantity(Math.max(1, newQuantity)); // Ensure quantity is at least 1

    };

    const baseUrl = window.APP_CONFIG?.PUBLIC_URL || '';
    const originUrl = window.APP_CONFIG?.ORIGIN_URL || '';

    // const productUrlLink = product
    //     ? `${originUrl}/product/${product.id}/${formatProductNameForUrl(product.product_name)}`
    //     : '';

    const productUrlLink = product
        ? `${baseUrl}/product/${product.id}/${formatProductNameForUrl(product.product_name)}`
        : '';

    // Build share URL & SEO
    const shareableUrl =
        `${(window as any).APP_CONFIG?.ORIGIN_URL || ""}/product/${product.id}/${formatProductNameForUrl(
            product.product_name
        )}` || window.location.href;

    const productImage =
        product?.media && product.media.length > 0 && product.media[0].original_url
            ? product.media[0].original_url
            : `${window.location.origin}${(window as any).APP_CONFIG?.PUBLIC_URL || ""
            }/images/default_product_image.png`;

    const productNameNormal = product?.product_name || "Product";
    const productDescription = product?.product_desc || "No description available";

    const SocialShareButtons: React.FC<{
        url: string;
        text: string;
    }> = ({ url, text }) => {
        const encodedUrl = encodeURIComponent(url);
        const shareText = encodeURIComponent(text);

        return (
            <div className="social-share2 flex flex-row  justify-center mt-0  gap-4 ">
                <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-2"
                    aria-label="Share on Facebook"
                >
                    <IconContext.Provider value={{ size: "1.6em", color: "#1877F2" }}>
                        <FaFacebook />
                    </IconContext.Provider>
                </a>

                <a
                    href={`https://social-plugins.line.me/lineit/share?url=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-2"
                    aria-label="Share on LINE"
                >
                    <IconContext.Provider value={{ size: "1.6em", color: "#00C300" }}>
                        <FaLine />
                    </IconContext.Provider>
                </a>
                <a
                    href={`https://t.me/share/url?url=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-2"
                    aria-label="Share on Telegram"
                >
                    <IconContext.Provider value={{ size: "1.6em", color: "#0088cc" }}>
                        <FaTelegram />
                    </IconContext.Provider>
                </a>
                {/* WeChat deep links only open inside WeChat; keep for parity */}
                <a
                    href={`weixin://dl/officialaccounts?scene=108&need_open_webview=0&url=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-2"
                    aria-label="Share on WeChat"
                >
                    <IconContext.Provider value={{ size: "1.6em", color: "#1AAD19" }}>
                        <IoLogoWechat />
                    </IconContext.Provider>
                </a>
                <a
                    href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${shareText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Twitter/X"
                    className="text-blue-400"
                >
                    <FaTwitter size={28} />
                </a>
                <a
                    href={`https://api.whatsapp.com/send?text=${shareText}%0A%0A${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on WhatsApp"
                    className="text-green-500"
                >
                    <FaWhatsapp size={28} />
                </a>
            </div>
        );
    };

    // --- Effect for Escape key and outside click ---
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            // If modifier modal is open, it should be closed first.
            if (showModifierModal) {
                handleCloseModifierModal();
            } else {
                onClose();
            }
        }
    }, [onClose, showModifierModal, handleCloseModifierModal]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
        // Do not close the product modal if the modifier modal is open
        if (showModifierModal) {
            return;
        }
        if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
            onClose();
        }
    }, [onClose, showModifierModal]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleKeyDown, handleClickOutside]);
    // --- End Effect ---

    type ProductFlag = 'NEW' | 'PROMOTION' | 'HOT';

    // console.log("Rendering ProductModal for product:", product);

    const flags: ProductFlag[] = [];
    if (product.is_new_active) flags.push("NEW");
    if (product.is_promotion_active) flags.push("PROMOTION");
    if (product.is_hot_active) flags.push("HOT");

    const flagStyles: Record<ProductFlag, string> = {
        NEW: "bg-green-500 text-white animate-jump-in",
        PROMOTION: "bg-yellow-400 text-red-900 animate-wiggle",
        HOT: "bg-red-600 text-white animate-pulse animate-infinite animate-ease-in-out",
    };

    // --- Modal Render Logic ---
    return (
        <> {/* Use Fragment because CustomerProfileModal provides its own portal/container */}
            <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-40 p-4"> {/* ProductModal Overlay */}
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-6"
                    ref={modalContentRef}
                > {/* ProductModal Container */}

                    <div className="flex justify-end"> {/* Attach ref to the content div */}
                        {/* Close Button for ProductModal */}
                        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl z-10">
                            <FiX size={24} />
                        </button>
                    </div>


                    {/* Product Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Image Column */}
                        <div ref={imageRef}> {/* Add ref if needed for animation */}

                            {/* Badge(s) */}
                            <div className="absolute top-2 left-2 flex flex-col md:flex-row gap-1">
                                {flags.map((flag) => (
                                    <span
                                        key={flag}
                                        className={`px-3 py-1 text-xs font-bold uppercase rounded-full shadow-md ${flagStyles[flag]}`}
                                    >
                                        {flag}
                                    </span>
                                ))}
                            </div>

                            <img
                                src={product.media.length > 0 ? product.media[0].original_url : 'placeholder.jpg'}
                                alt={product.product_name}
                                className="w-full h-auto object-contain rounded"
                            />

                        </div>

                        {/* Details Column */}
                        <div>

                            <h2 className="text-2xl font-bold mb-2">
                                <a href={`${productUrlLink}`} target='_blank' rel="noopener noreferrer" className="text-blue-600 hover:underline"
                                >
                                    {product.product_name}
                                </a>
                            </h2>

                            <p className="text-sm text-gray-500 mb-4">SKU: {product.product_code}</p>

                            {/* Price Display */}
                            {settings?.themes?.displayPrice === true ? (

                                <div className="price-div  mb-4">
                                    {effectiveUserIsAuthenticated && mtPriceData ? (
                                        <p className="text-lg text-green-600 font-semibold">
                                            Your VIP Price: <span className='font-bold'>{displayPrice(mtPriceData.price)}</span>
                                        </p>
                                    ) : null}

                                    <p className="text-sm text-gray-700">
                                        <span>{t('productCard.srp')}:&nbsp;</span>

                                        {product.SRP > 0 && product.SRP !== product.product_value ? (
                                            <span className='text-gray-400 font-thin text-lg line-through'>{displayPrice(product.SRP)}</span>
                                        ) : null}
                                        <span className='text-gray-600 font-semibold text-lg  px-2'>{displayPrice(product.product_value)}</span>
                                    </p>
                                </div>
                            ) : null}

                            {/* Description */}
                            <p className="text-gray-700 mb-4">{product.product_desc || 'No description available.'}</p>

                            {/* Stock/Warranty/Link Info */}
                            <div className='flex flex-col mb-4'>
                                <div className="flex flex-row justify-between mt-2 ">
                                    {
                                        product?.stock !== undefined && product?.stock > 0 ?
                                            <span className="productCardStock px-2 py-1 my-1 text-white bg-green-600 border rounded-lg shadow">
                                                In Stock
                                                {settings?.themes?.displayStockQty === true ? (

                                                    <span className='stock-qty'>&nbsp;{product?.stock}</span>
                                                ) : null}
                                            </span> :
                                            <span className="productCardStock px-2 py-1 my-1 text-white bg-orange-600 border rounded-lg shadow">
                                                Back Order
                                                {settings?.themes?.displayStockQty === true ? (
                                                    <span className='backorder-qty'>&nbsp;{product?.stock}</span>
                                                ) : null}
                                            </span>
                                    }

                                    {
                                        product?.restock !== undefined && product?.restock > 0 &&
                                        <span className="productCardStock px-2 py-1 my-1 text-white bg-purple-600 border rounded-lg shadow flex flex-row">
                                            Re-Stock
                                            {/* <pre>{JSON.stringify({ displayRestockAt: settings?.themes?.displayRestockAt, restock_at: product.restock_at })}</pre> */}

                                            {settings?.themes?.displayStockQty === true ? (
                                                <>
                                                    <span className='restock-qty'>&nbsp;{product?.restock}</span>
                                                </>
                                            ) : null}
                                            {settings?.themes?.displayRestockAt === true && product.restock_at ? (
                                                <span className='restock-dt'>
                                                    &nbsp;on {new Date(product.restock_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                </span>
                                            ) : null}
                                        </span>
                                    }

                                    <div className='flex flex-row justify-between items-center mt-2'>

                                        {product.product_link &&
                                            <a href={product.product_link} className='mr-2' target="_blank" rel="noreferrer" data-tooltip-id={`link-${product.id}`} data-tooltip-content={`External ${product.product_name} Product Details Link`}><IconContext.Provider value={{ size: "1em", color: "purple" }}>
                                                <FaLink /></IconContext.Provider>
                                            </a>}

                                        {product.product_link &&
                                            <ReactTooltip id={`link-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />}

                                        {product.warranty &&
                                            <div data-tooltip-id={`warranty-${product.id}`} data-tooltip-content={`${product.warranty} warranty`}>
                                                <IconContext.Provider value={{ size: "1em", color: "brown" }}><FaShieldHeart /></IconContext.Provider>
                                            </div>}

                                        {/* {product.warranty && 
                                        <ReactTooltip id={`warranty-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />} */}

                                    </div>

                                </div>



                                {/* Add to Cart Button */}
                                {effectiveUserIsAuthenticated && (
                                    <div className="flex items-center mt-4">
                                        {/* Quantity Adjuster */}
                                        <div className="flex items-center mr-3">
                                            <button
                                                onClick={() => handleQuantityChange(quantity - 1)}
                                                className="px-3 py-2 border border-gray-300 rounded-l-md hover:bg-gray-100 focus:outline-none"
                                                disabled={isLoadingCustomerForAction || quantity <= 1}
                                            >
                                                -
                                            </button>
                                            <input
                                                type="number"
                                                value={quantity}
                                                onChange={(e) => handleQuantityChange(parseInt(e.target.value, 10) || 1)}
                                                className="w-16 text-center border-t border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 py-2"
                                                min="1"
                                                disabled={isLoadingCustomerForAction}
                                            />
                                            <button
                                                onClick={() => handleQuantityChange(quantity + 1)}
                                                className="px-3 py-2 border border-gray-300 rounded-r-md hover:bg-gray-100 focus:outline-none"
                                                disabled={isLoadingCustomerForAction}
                                            >
                                                +
                                            </button>
                                        </div>
                                        {/* Add to Cart Button */}
                                        <button
                                            onClick={handleModalAddToCartClick}
                                            disabled={isLoadingCustomerForAction}
                                            className={`flex-grow px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-150 ${isLoadingCustomerForAction ? 'opacity-50 cursor-wait' : ''}`}
                                        >
                                            {isLoadingCustomerForAction ? 'Checking...' : 'Add to Cart'}
                                        </button>
                                    </div>
                                )}
                                {!effectiveUserIsAuthenticated && (
                                    <p className="text-sm text-red-500 mt-2">Please log in to add items to CART.</p>
                                )}
                            </div>

                            {/* Social Share */}
                            <div className="mt-6">
                                <SocialShareButtons
                                    url={shareableUrl}
                                    text={`Check out this product: ${product?.product_name}`}
                                />
                            </div>

                        </div>


                    </div>
                    <ProductDetails product={product} /> {/* Use your new component here */}

                </div>
            </div>

            {/* --- Render Customer Profile Modal Conditionally --- */}
            {/* This modal will render via its own portal structure */}
            {showCustomerProfileModal && customerForAction && (
                <CustomerProfileModal
                    initialCustomerData={customerForAction}
                    onClose={handleCustomerProfileModalClose}
                />
            )}

            {/* --- Render Modifier Modal Conditionally (if triggered by handleAddToCart) --- */}
            {/* This modal also likely renders via its own portal/structure */}
            {showModifierModal && selectedProduct && (
                <ModifierModal
                    product={selectedProduct}
                    modifiers={modifiers}
                    onClose={handleCloseModifierModal}
                    onModifierSelection={handleModifierSelection}
                    onConfirm={handleConfirmModifiers}
                    selectedModifiers={selectedModifiers}
                />
            )}

        </>
    );
};

export default ProductModal;
